import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocationContext } from '../../context/LocationContext';
import { useEntityStats } from '../../context/EntityStatsContext';
import {
    crmCatalogApi,
    type GhlArchiveSummary,
    type GhlCleanupPreview,
} from '../../api/crmCatalogApi';
import { ConfirmDialog } from './ConfirmDialog';
import { Toast } from './Toast';

type ToastState = { message: string; type: 'success' | 'error' | 'info' } | null;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * Old GHL mappings of a practice that switched to AV EMR.
 *
 * - AV EMR practice with GHL-targeted mappings left in the engine: "N old GHL mappings"
 *   + "Clean up" (confirmation lists every row, then archives and removes them).
 * - GHL practice with archived mappings (switched back): "Restore GHL mappings".
 *
 * Nothing here runs without an explicit click + confirmation.
 */
export const GhlMappingCleanup: React.FC = () => {
    const { currentLocation } = useLocationContext();
    const { refresh: refreshStats } = useEntityStats();
    const ghId = currentLocation?.ghl_id;
    const isAvEmr = currentLocation?.crm === 'av_emr';

    const [preview, setPreview] = useState<GhlCleanupPreview | null>(null);
    const [archive, setArchive] = useState<GhlArchiveSummary | null>(null);
    const [confirmCleanup, setConfirmCleanup] = useState(false);
    const [confirmRestore, setConfirmRestore] = useState(false);
    const [busy, setBusy] = useState(false);
    const [toast, setToast] = useState<ToastState>(null);

    const load = useCallback(async () => {
        setPreview(null);
        setArchive(null);
        if (!ghId) return;
        try {
            if (isAvEmr) {
                setPreview(await crmCatalogApi.previewGhlCleanup(ghId));
            } else {
                setArchive(await crmCatalogApi.getGhlArchive(ghId));
            }
        } catch {
            // Engine DB off, CRM changed mid-way, or no access: show nothing.
        }
    }, [ghId, isAvEmr]);

    useEffect(() => { void load(); }, [load]);

    const handleCleanup = async () => {
        if (!ghId) return;
        setBusy(true);
        try {
            const r = await crmCatalogApi.applyGhlCleanup(ghId);
            const removed = Object.values(r.removed).reduce((sum, n) => sum + n, 0);
            const dismissed = r.suggestions_dismissed.practitioner + r.suggestions_dismissed.appointment_type;
            const parts = [plural(removed, 'engine mapping')];
            if (dismissed) parts.push(plural(dismissed, 'pending GHL suggestion'));
            setToast({ message: `Cleaned up ${parts.join(' and ')}. Archived for restore if the practice moves back to GHL.`, type: 'success' });
            setConfirmCleanup(false);
            refreshStats();
            await load();
        } catch (err) {
            setToast({ message: err instanceof Error ? err.message : 'Failed to clean up old GHL mappings', type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const handleRestore = async () => {
        if (!ghId) return;
        setBusy(true);
        try {
            const r = await crmCatalogApi.restoreGhlMappings(ghId);
            const restored = r.restored.engine + r.restored.suggestions;
            const msg = r.skipped.length
                ? `Restored ${plural(restored, 'GHL mapping')}; ${r.skipped.length} skipped because they conflict with current mappings.`
                : `Restored ${plural(restored, 'GHL mapping')}.`;
            setToast({ message: msg, type: r.skipped.length ? 'info' : 'success' });
            setConfirmRestore(false);
            refreshStats();
            await load();
        } catch (err) {
            setToast({ message: err instanceof Error ? err.message : 'Failed to restore GHL mappings', type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const showCleanup = isAvEmr && preview && preview.total > 0;
    const showRestore = !isAvEmr && archive?.can_restore;

    return (
        <>
            {showCleanup && preview && (
                <div className="flex items-center justify-between gap-4 bg-mapper-amber-bg border border-mapper-border rounded-[14px] px-5 py-4 mb-[22px]">
                    <div className="min-w-0">
                        <div className="text-[14px] font-bold text-mapper-amber">
                            {plural(preview.total, 'old GHL mapping')}
                        </div>
                        <div className="text-[12.5px] text-mapper-muted-2 leading-[1.4]">
                            This practice now uses AV EMR, but some mappings still point at GHL users or calendars.
                            Clean them up so AV EMR providers and services can be mapped.
                        </div>
                    </div>
                    <button
                        onClick={() => setConfirmCleanup(true)}
                        className="shrink-0 px-4 py-2 rounded-[9px] text-[13px] font-semibold bg-mapper-teal text-white shadow-mapper-btn"
                    >
                        Clean up
                    </button>
                </div>
            )}

            {showRestore && archive && (
                <div className="flex items-center justify-between gap-4 bg-mapper-white border border-mapper-border rounded-[14px] px-5 py-4 mb-[22px]">
                    <div className="min-w-0">
                        <div className="text-[14px] font-bold">
                            {plural(archive.restorable_archive_rows, 'archived GHL mapping')}
                        </div>
                        <div className="text-[12.5px] text-mapper-muted-2 leading-[1.4]">
                            Removed when this practice used AV EMR. It is on GHL again, so they can be put back.
                        </div>
                    </div>
                    <button
                        onClick={() => setConfirmRestore(true)}
                        className="shrink-0 px-4 py-2 rounded-[9px] text-[13px] font-semibold bg-mapper-teal text-white shadow-mapper-btn"
                    >
                        Restore GHL mappings
                    </button>
                </div>
            )}

            {confirmCleanup && preview && (
                <CleanupConfirmModal
                    preview={preview}
                    busy={busy}
                    onCancel={() => setConfirmCleanup(false)}
                    onConfirm={handleCleanup}
                />
            )}

            <ConfirmDialog
                open={confirmRestore}
                title="Restore GHL mappings?"
                message={`Put back ${plural(archive?.restorable_archive_rows ?? 0, 'archived row')} (GHL calendar mappings, provider mappings, default calendar and pending suggestions). Rows that conflict with current mappings are skipped. Nothing is approved automatically.`}
                confirmLabel="Restore"
                busy={busy}
                onConfirm={handleRestore}
                onCancel={() => setConfirmRestore(false)}
            />

            {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
        </>
    );
};

const Section: React.FC<{ title: string; items: string[] }> = ({ title, items }) => {
    if (items.length === 0) return null;
    return (
        <div className="mb-4">
            <div className="text-[12px] font-bold tracking-[0.4px] text-mapper-muted-2 mb-[6px]">
                {title} ({items.length})
            </div>
            <ul className="m-0 pl-4 text-[13px] text-mapper-body-text-3 leading-[1.55]">
                {items.map((item, i) => <li key={i} className="break-words">{item}</li>)}
            </ul>
        </div>
    );
};

const CleanupConfirmModal: React.FC<{
    preview: GhlCleanupPreview;
    busy: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}> = ({ preview, busy, onCancel, onConfirm }) => {
    const label = (name: string | null | undefined, id: string | null | undefined) =>
        name ? `${name} (${id ?? '—'})` : (id || '—');

    const calendarItems = preview.calendar_associations.map(r =>
        `${label(r.calendar_name, r.calendar_ghl_id)} · ${r.direction === 'emr_to_ghl' ? 'EMR → GHL' : 'GHL → EMR'}${r.emr ? ` · ${r.emr}` : ''}`,
    );
    const providerItems = preview.providers.map(r =>
        `${r.name || `EMR provider ${r.emr_id ?? r.id}`} → ${label(r.ghl_user_name, r.ghl_id)}`,
    );
    const providerCalendarItems = preview.provider_calendars.map(r =>
        `${r.name || `Provider ${r.id}`}: calendar ${label(r.calendar_name, r.calendar_ghl_id)} (cleared, provider mapping kept)`,
    );
    const defaultItems = preview.default_calendars.map(r =>
        `${r.emr}: ${label(r.calendar_name, r.calendar_ghl_id)} (cleared)`,
    );
    const defaultProviderItems = [
        ...preview.default_providers.map(r =>
            `${r.emr} default provider: ${label(r.ghl_user_name, r.ghl_user_id)} (cleared)`,
        ),
        ...preview.association_default_providers.map(r =>
            `${label(r.calendar_name, r.calendar_ghl_id)} default provider: ${label(r.ghl_user_name, r.ghl_user_id)} (cleared)`,
        ),
    ];
    const suggestionItems: string[] = [];
    if (preview.pending_suggestions.practitioner) {
        suggestionItems.push(`${plural(preview.pending_suggestions.practitioner, 'pending provider suggestion')} for a GHL user`);
    }
    if (preview.pending_suggestions.appointment_type) {
        suggestionItems.push(`${plural(preview.pending_suggestions.appointment_type, 'pending appointment type suggestion')} for a GHL calendar`);
    }
    const noMatch = preview.pending_suggestions.practitioner_no_match + preview.pending_suggestions.appointment_type_no_match;
    if (noMatch) {
        const when = preview.catalog_first_loaded_at
            ? `made before the AV EMR catalog was first loaded (${new Date(preview.catalog_first_loaded_at).toLocaleDateString()})`
            : 'made before AV EMR (first catalog load time unknown, so all of them)';
        suggestionItems.push(`${plural(noMatch, 'pending "no match" suggestion')} ${when}`);
    }

    return createPortal(
        <div
            onClick={busy ? undefined : onCancel}
            className="mapper-ui font-sans fixed inset-0 flex items-center justify-center z-[120] p-5"
            style={{ background: 'rgba(24,33,48,.42)' }}
        >
            <div
                onClick={e => e.stopPropagation()}
                className="bg-mapper-white rounded-2xl max-w-[560px] w-full overflow-hidden shadow-mapper-modal text-mapper-text flex flex-col max-h-[85vh]"
            >
                <div className="px-6 py-5 border-b border-mapper-border-3 flex items-center justify-between gap-4">
                    <h3 className="text-base font-bold m-0">Clean up old GHL mappings?</h3>
                    <button
                        onClick={onCancel}
                        disabled={busy}
                        aria-label="Close"
                        className="w-[30px] h-[30px] rounded-lg text-mapper-muted-3 text-xl flex items-center justify-center shrink-0 disabled:opacity-50"
                    >
                        ×
                    </button>
                </div>

                <div className="px-6 py-[18px] overflow-y-auto">
                    <div className="text-sm text-mapper-body-text-3 leading-[1.6] mb-4">
                        These point at GHL ids, which AV EMR cannot use. They are archived first and can be restored
                        if the practice switches back to GHL. Status mappings and approved suggestions are not touched,
                        and nothing is approved automatically.
                    </div>
                    <Section title="CALENDAR MAPPINGS REMOVED" items={calendarItems} />
                    <Section title="PROVIDER MAPPINGS REMOVED" items={providerItems} />
                    <Section title="PROVIDER CALENDARS CLEARED" items={providerCalendarItems} />
                    <Section title="DEFAULT CALENDAR CLEARED" items={defaultItems} />
                    <Section title="DEFAULT PROVIDERS CLEARED" items={defaultProviderItems} />
                    <Section title="SUGGESTIONS DISMISSED (AI CAN RE-SUGGEST AV EMR TARGETS)" items={suggestionItems} />
                </div>

                <div className="px-6 py-4 border-t border-mapper-border-3 flex items-center justify-end gap-3">
                    <button
                        onClick={onCancel}
                        disabled={busy}
                        className="px-4 py-2 rounded-[9px] text-[13px] font-semibold text-mapper-muted disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={busy}
                        className="px-4 py-2 rounded-[9px] text-[13px] font-semibold disabled:opacity-70 bg-mapper-red text-white"
                    >
                        {busy ? 'Working…' : `Clean up ${plural(preview.total, 'item')}`}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};
